var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
function supportsMediaDevices() {
  return typeof navigator !== "undefined" && !!navigator.mediaDevices;
}
function getMediaDevicesApi() {
  if (!supportsMediaDevices()) {
    throw new Error(`The media devices API isn't supported here.`);
  }
  return navigator.mediaDevices;
}
async function enumerateDevices() {
  const devices = await getMediaDevicesApi().enumerateDevices();
  return devices.filter(isPhysicalDevice).map(normalizeDeviceInfo);
}
function isPhysicalDevice(device) {
  return device.deviceId !== "default";
}
function normalizeDeviceInfo(device) {
  return {
    label: device.label || null,
    kind: device.kind,
    deviceId: device.deviceId || null,
    groupId: device.groupId || null
  };
}
var DeviceKind = /* @__PURE__ */ ((DeviceKind2) => {
  DeviceKind2["VideoInput"] = "videoinput";
  DeviceKind2["AudioInput"] = "audioinput";
  DeviceKind2["AudioOutput"] = "audiooutput";
  return DeviceKind2;
})(DeviceKind || {});
async function getUserMedia(constraints) {
  return getMediaDevicesApi().getUserMedia(constraints);
}
class DeviceManager {
  constructor() {
    __publicField(this, "_knownDevices", []);
    __publicField(this, "_gainedScreenAccessOnce", false);
    __publicField(this, "ondevicechange", null);
    __publicField(this, "getUserMedia", async (constraints) => {
      const stream = await getUserMedia(constraints);
      this.enumerateDevices();
      return stream;
    });
    __publicField(this, "getDisplayMedia", async (constraints) => {
      const stream = await getMediaDevicesApi().getDisplayMedia(constraints);
      if (!this._gainedScreenAccessOnce) {
        this._gainedScreenAccessOnce = true;
        this.enumerateDevices();
      }
      return stream;
    });
    __publicField(this, "enumerateDevices", async () => {
      const devices = await enumerateDevices();
      this._checkForDeviceChanges(devices);
      return devices;
    });
    __publicField(this, "getSupportedConstraints", () => {
      return getMediaDevicesApi().getSupportedConstraints();
    });
    if (supportsMediaDevices()) {
      getMediaDevicesApi().addEventListener("devicechange", () => {
        if (this.ondevicechange) {
          return this.enumerateDevices();
        }
        return Promise.resolve();
      });
    }
  }
  _checkForDeviceChanges(newDevices) {
    var _a;
    const oldDevices = this._knownDevices;
    this._knownDevices = newDevices;
    const changes = this._calculateDeviceDiff(newDevices, oldDevices);
    if (changes.length) {
      (_a = this.ondevicechange) == null ? void 0 : _a.call(this, { changes, devices: newDevices });
    }
  }
  _calculateDeviceDiff(newDevices, oldDevices) {
    const removals = oldDevices.slice();
    const updates = [];
    const additions = newDevices.filter((newDevice) => {
      const oldDeviceIndex = removals.findIndex((oldDevice) => {
        return isIdenticalDevice(newDevice, oldDevice);
      });
      if (oldDeviceIndex > -1) {
        const [oldDevice] = removals.splice(oldDeviceIndex, 1);
        if (newDevice.label !== oldDevice.label) {
          const update = {
            type: OperationType.Update,
            newInfo: newDevice,
            oldInfo: oldDevice
          };
          updates.push(update);
        }
      }
      return oldDeviceIndex === -1;
    });
    return [
      ...updates,
      ...removals.map((device) => {
        return { type: OperationType.Remove, device };
      }),
      ...additions.map((device) => {
        return { type: OperationType.Add, device };
      })
    ];
  }
}
function isIdenticalDevice(newDevice, oldDevice) {
  if (oldDevice.deviceId) {
    return newDevice.deviceId === oldDevice.deviceId;
  }
  function toCrudeId(device) {
    return `${device.kind}:${device.groupId}`;
  }
  return toCrudeId(newDevice) === toCrudeId(oldDevice);
}
var OperationType = /* @__PURE__ */ ((OperationType2) => {
  OperationType2["Add"] = "add";
  OperationType2["Remove"] = "remove";
  OperationType2["Update"] = "update";
  return OperationType2;
})(OperationType || {});
var index = new DeviceManager();
export { DeviceKind, OperationType, index as default, supportsMediaDevices };
//# sourceMappingURL=media-devices.es.js.map
